/**
 * 環状マップ（I41）。≤20 kb で読める円。切断・注釈・選択弧。
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  featureSpans,
  type GenomicFeatureV1,
  type Interval,
  type RestrictionCut,
} from '@kensuke/genome-core';
import { featureColor } from './geneAnalysisLayout';

export interface GeneAnalysisCircularMapProps {
  length: number;
  features: GenomicFeatureV1[];
  selection: Interval | null;
  cuts: RestrictionCut[];
  onSelect: (interval: Interval) => void;
  onFeatureActivate?: (feature: GenomicFeatureV1) => void;
  theme: 'light' | 'dark';
}

export default function GeneAnalysisCircularMap({
  length,
  features,
  selection,
  cuts,
  onSelect,
  onFeatureActivate,
  theme,
}: GeneAnalysisCircularMapProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState(280);
  const pixelRatio = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) setSize(Math.max(220, Math.min(width, 520)));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const visible = useMemo(() => features.slice(0, 80), [features]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || length <= 0) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    canvas.width = Math.round(size * pixelRatio);
    canvas.height = Math.round(size * pixelRatio);
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

    const isDark = theme === 'dark';
    const cx = size / 2;
    const cy = size / 2;
    const radius = size * 0.38;
    context.clearRect(0, 0, size, size);
    context.fillStyle = isDark ? '#0B1220' : '#F8FAFC';
    context.fillRect(0, 0, size, size);

    context.strokeStyle = isDark ? '#334155' : '#CBD5E1';
    context.lineWidth = 10;
    context.beginPath();
    context.arc(cx, cy, radius, 0, Math.PI * 2);
    context.stroke();

    const angleOf = (base: number) => (base / length) * Math.PI * 2 - Math.PI / 2;

    if (selection && selection.end > selection.start) {
      context.strokeStyle = isDark ? 'rgba(56,189,248,0.7)' : 'rgba(37,99,235,0.55)';
      context.lineWidth = 16;
      context.beginPath();
      context.arc(cx, cy, radius, angleOf(selection.start), angleOf(Math.min(selection.end, length)), false);
      context.stroke();
    }

    for (const feature of visible) {
      for (const span of featureSpans(feature, length)) {
        context.strokeStyle = featureColor(feature);
        context.lineWidth = 8;
        context.beginPath();
        context.arc(cx, cy, radius - 16, angleOf(span.start), angleOf(span.end), false);
        context.stroke();
      }
    }

    for (const cut of cuts.slice(0, 80)) {
      const angle = angleOf(cut.cut);
      context.strokeStyle = cut.unique ? '#16A34A' : '#F97316';
      context.lineWidth = 1.5;
      context.beginPath();
      context.moveTo(cx + Math.cos(angle) * (radius - 28), cy + Math.sin(angle) * (radius - 28));
      context.lineTo(cx + Math.cos(angle) * (radius + 18), cy + Math.sin(angle) * (radius + 18));
      context.stroke();
    }

    context.fillStyle = isDark ? '#E2E8F0' : '#1F2937';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.font = '12px system-ui, sans-serif';
    context.fillText(`${length.toLocaleString()} bp`, cx, cy);
  }, [cuts, features, length, pixelRatio, selection, size, theme, visible]);

  const pick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || length <= 0) return;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;
    const angle = (Math.atan2(y, x) + Math.PI / 2 + Math.PI * 2) % (Math.PI * 2);
    const base = Math.floor((angle / (Math.PI * 2)) * length);
    const hit = visible.find((feature) =>
      featureSpans(feature, length).some((span) => base >= span.start && base < span.end)
    );
    if (hit && onFeatureActivate) {
      onFeatureActivate(hit);
      return;
    }
    onSelect({ start: base, end: base + 1 });
  };

  return (
    <div ref={containerRef} className="ga-circular-map" data-testid="gene-analysis-circular-map">
      <canvas ref={canvasRef} onClick={pick} />
    </div>
  );
}
